import { Button } from "antd";
import { FileUploadRules } from "../../types/field";
import { useEffect, useState } from "react";
import { message, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from 'antd';
import type { RcFile } from 'rc-upload/lib/interface';
import axios from "axios";
import { authHeader } from "../../utils/utils";
import ImageComponent from "../../components/Image/imageComponent";
import DocumentComponent from "../../components/Image/documentComponent";
import { UrlConstants } from "../../utils/ApiConstants";
import { GetRequest, replaceWithCurrentApiVersion } from "../../utils/api";
import { Notification } from "../../components/notification/notification";
import VideoComponent from "../../components/Image/videoComponent";
import pdfIcon from  "../../assets/images/pdf-icon.png";


type CustomFileUploadProps = {
  title: string;
  placeholder?: string;
  onChange?: any;
  disabled?: boolean;
  data?: any;
  className?: string;
  rules?: FileUploadRules;
  register?: any;
  id: string;
  name: string;
  setValue?: any;
  control: any;
  uploadApi?: string;
  downloadApi?: string;
  maxFilesAllowed?: number;
  childEntity: string;
  width: number;
  uploadedFileList?: any;
  getValue?: any;
  handleAutoCompleteAction?: Function;
};

const CustomFileUpload: React.FC<CustomFileUploadProps> = ({
  data,
  disabled,
  rules,
  id,
  name,
  uploadApi,
  maxFilesAllowed = 5,
  downloadApi,
  uploadedFileList,
  onChange: inputOnChange,
  handleAutoCompleteAction
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  // const [previewUrl, setPreviewUrl] = useState<any>('');
  const [previewType, setPreviewType] = useState('');
  const [uploadedFileIds, setUploadedFileIds] = useState<{ fileName: string }[]>([]);

  //Fallback if uploadfilelist is passed instead of data
  if (!data && uploadedFileList && uploadedFileList.length > 0) {
    data = uploadedFileList;
  }

  const isImage = (url: string = "") => /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
  const isPDF = (url: string = "") => /\.(pdf)$/.test(url);
  const isVideo = (url: string = "") => /\.(mp4)$/.test(url);

  const getUploadedFiles = async (file: string) => {

    let type = 'image';

    if (isImage(file)) {
      type = 'image';
    } else if (isPDF(file)) {
      type = 'pdf';
    } else if (isVideo(file)) {
      type = 'video';
    }

    if (downloadApi && downloadApi.length > 0) {

      try {

        let endpoint = downloadApi + "/" + file;
        let response = await GetRequest(endpoint, type === 'image' ? "arraybuffer" : 'blob');

        let imageSrc: any;

        if (type === 'image') {
          let blob = new Blob([response], { type: 'image/jpeg' });
          imageSrc = URL.createObjectURL(blob);
        } else {
          imageSrc = URL.createObjectURL(response);
        }

        imageSrc = String(imageSrc);


        if (type === 'pdf') {
          setFileList(prev => [...prev, {
            uid: (Math.random() * 1000).toFixed(2),
            name: file,
            status: 'done',
            url: imageSrc,
            thumbUrl: pdfIcon
          }]);
        } else {
          setFileList(prev => [...prev, {
            uid: (Math.random() * 1000).toFixed(2),
            name: file,
            status: 'done',
            url: imageSrc
          }]);

        }

      } catch (error) {
        console.error('Error fetching file:', error);
        Notification('error', "Failed to load file");
      }
    }
  };

  useEffect(() => {

    if (data && data.length > 0) {

      if (typeof data === 'string') {

        getUploadedFiles(data);

        if (!uploadedFileIds.some((item) => item.fileName === data)) {
         
          setUploadedFileIds((prevState) => {
            const updatedFileIds = [...prevState, { fileName: data }];
            inputOnChange(updatedFileIds);
            return updatedFileIds;
          });

        }

      } else if (data.length > 0) {

        data.forEach((file: any) => {


          if(fileList.some((item) => item.name === file.fileName)) {
            return;
          }

          getUploadedFiles(file.fileName);

          if (!uploadedFileIds.some((item) => item.fileName === file.fileName)) {
            setUploadedFileIds(prevState => {
              const updatedFileIds = [...prevState, { fileName: file.fileName }];
              inputOnChange(updatedFileIds);
              return updatedFileIds;
            });
          }



        });

      }
    }
  }, [data]);

  const validateFileSize = (fileSize: number) => {
    return parseInt((fileSize! / 1024).toFixed(2)) <= Number(rules?.maxSize);
  };

  const customUploadAction = async (options: any) => {
    const { onSuccess, onError, file } = options;

    if (handleAutoCompleteAction) {
      handleAutoCompleteAction(file, name);
    }
    const uploadURL = UrlConstants.BASE_URL + replaceWithCurrentApiVersion(uploadApi ?? "");

    const formData = new FormData();
    formData.append("fileName", file);
    formData.append("fieldName", id);

    try {
      const response = await axios({
        method: "post",
        url: uploadURL,
        data: formData,
        headers: {
          authorization: `${authHeader().Authorization}`,
        }
      });

      const fileId = response.data.fileId;

      setUploadedFileIds(prevState => {
        const updatedFileIds = [...prevState, { fileName: fileId }];
        
        inputOnChange(updatedFileIds);

        return updatedFileIds;
      });

      onSuccess(response.data);
      return response;

    } catch (err: any) {
      console.error("Upload error:", err);
      Notification("error", "Failed to Upload file, Please try again!");

      if (err.response?.data?.errorMessages) {
        err.response.data.errorMessages.forEach((errorMessage: any) => {
          message.error(errorMessage.message);
        });
      }

      onError({ err });
      return false;
    }
  };

  const [previewImage, setPreviewImage] = useState<any>('');

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });

  const handlePreview = async (file: UploadFile) => {

    let type = "image";

    if (file.name?.includes(".jpg") || file.name?.includes('.jpeg') || file.name?.includes('.png')) {
      type = "image/";
    } else if (file.name.includes(".mp4")) {
      type = "video";
    } else {
      type = "application/pdf";
    }

    if (type.includes("image")) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as RcFile);
      }

      if (file.hasOwnProperty("originFileObj")) {
        setPreviewImage(URL.createObjectURL(file.originFileObj as RcFile));
      } else {
        setPreviewImage(file.url || (file.preview as string));
      }
    } else if (type === "application/pdf") {
      
      if (file.hasOwnProperty("originFileObj")) {
        setPreviewImage(URL.createObjectURL(file.originFileObj as RcFile));
      } else {
        setPreviewImage(file.url);
      }
      
    } else if (type === "video") {
      
      if (file.hasOwnProperty("originFileObj")) {
        setPreviewImage(URL.createObjectURL(file.originFileObj as RcFile));
      } else {
        setPreviewImage(file.url as string);
      }
    }

    setPreviewType(type);
  };

  const onPreviewClosed = () => {
    setPreviewType('');
  };

  return (
    <>
      <div>
        <div>
          <Upload
            customRequest={customUploadAction}
            listType="picture"
            disabled={disabled}
            onRemove={(deletedFile) => {

              const newFileIds = uploadedFileIds.filter((id) => id.fileName !== deletedFile.name);
              
              setUploadedFileIds(newFileIds);

              const newFileList = fileList.filter((file) => file.name !== deletedFile.name);
              setFileList(newFileList);

              if (inputOnChange) {
                inputOnChange(newFileIds);
              }

            }}

            beforeUpload={(file: UploadFile) => {
              if (!validateFileSize(file.size!)) {
                message.error(`${file.name} is too large`);
                return false;
              }
            }}
            fileList={fileList}
            multiple={maxFilesAllowed > 1}
            maxCount={maxFilesAllowed}
            accept={rules?.allowFileFormat.split(",").map((item: string) => "." + item).toString()}
            defaultFileList={fileList.length > 0 ? fileList : undefined}
            onChange={(info) => {
              setFileList(info.fileList); // Update state with current fileList
            }}
            onPreview={handlePreview}
          >
            {!disabled &&
              <Button icon={<UploadOutlined />} disabled={fileList && fileList.length === maxFilesAllowed}>
                Click here to Upload
              </Button>
            }
          </Upload>

          {previewType === 'image/' && (
            <ImageComponent
              src={previewImage}
              alt="No Image"
              title={''}
              type='image'
              URLloaded={true}
              modelOpen={true}
              onPreviewClosed={onPreviewClosed}
            />
          )}

          {previewType === 'application/pdf' && (
            <DocumentComponent
              src={previewImage}
              alt="img_girl.jpg"
              title={''}
              type='document'
              URLloaded={true}
              modelOpen={true}
              onPreviewClosed={onPreviewClosed}
            />
          )}

          {previewType === "video" && (
            <VideoComponent onPreviewClosed={onPreviewClosed} src={previewImage} URLloaded={true} ></VideoComponent>
          )}
        </div>
      </div >
    </>
  );
}

export default CustomFileUpload;